<script lang="ts">
    import CloseIcon from '@amp/web-app-components/assets/icons/close.svg';
    import { getCookie } from '@amp/web-app-components/src/utils/cookie';
    import { focusNodeOnMount } from '@amp/web-app-components/src/actions/focus-node-on-mount';
    import DropdownMenu from '@amp/web-app-components/src/components/DropdownMenu/DropdownMenu.svelte';
    import { onMount } from 'svelte';
    import Modal from '@amp/web-app-components/src/components/Modal/Modal.svelte';
    import LocaleSwitcherModal from '@amp/web-app-components/src/components/Modal/LocaleSwitcherModal/LocaleSwitcherModal.svelte';
    import type { SvelteComponent } from 'svelte';
    import type { Locale } from '@amp/web-app-components/src/types';
    import type { Region } from '@amp/web-app-components/src/components/buttons/LocaleSwitcherButton/types';
    import type { StorefrontNames } from '@amp/web-app-components/src/components/banners/types';
    import { getStorefrontRoute } from '@amp/web-app-components/src/utils/getStorefrontRoute';
    import { maybeGetUniqueIdGenerator } from '@amp/web-app-components/src/utils/uniqueId';

    const uniqueIdGenerator = maybeGetUniqueIdGenerator();

    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;
    export let locale: Locale;
    export let user: { isSignedIn: boolean; userStorefront?: string | null };
    export let regions: Region[];
    export let storefrontNameTranslations: StorefrontNames;
    export let defaultRoute: string;
    export let dropdownPosition: 'up' | 'down' = 'up';
    export let dialogTitleId =
        uniqueIdGenerator?.() ?? 'away-from-home-banner-dialog-title';

    const DISMISSAL_KEY = 'dismissed-geo-banner';
    let modalTriggerElement = null;
    let modalElement: SvelteComponent;
    let geoCookie: string;
    let previouslyDismissedBanner: string;
    let options = [];
    let currentLanguage = locale.language;
    let storefront = locale.storefront.toLowerCase();
    let showBanner = false;

    // rdar://146412090 (Web component: Away from home banner onMount function not being called)
    // for the built web component implementation, we need to ensure the component is initialized
    $: user, inititalizeComponent();

    // if user changes, call toggleBannerCheck function
    $: user, toggleBannerCheck();

    const inititalizeComponent = () => {
        // check that we are not in SSR mode before initializing
        if (typeof localStorage !== 'undefined') {
            previouslyDismissedBanner = window.localStorage.getItem(
                `${DISMISSAL_KEY}-${storefront}`,
            );
            geoCookie = getCookie('geo')?.toLowerCase();
            toggleBannerCheck();
        }
    };

    const handleCloseButton = () => {
        showBanner = false;

        window.localStorage.setItem(
            `${DISMISSAL_KEY}-${storefront}`,
            Date.now().toString(),
        );
    };

    const toggleBannerCheck = () => {
        // if there is no geo cookie found or no translations for the geo cookie
        // or you are on your account storefront, do not show banner
        if (
            !geoCookie ||
            !storefrontNameTranslations[geoCookie] ||
            user?.userStorefront === storefront
        ) {
            showBanner = false;
        } else if (user?.isSignedIn && user?.userStorefront !== undefined) {
            // always show the storefront picker when you're signed in and not on your account storefront,
            // even if you've previously dismissed the banner
            // note: if user is signed in, userStorefront should be defined - but in the edge case that
            // value is not defined, default to skipping this condition and not showing the banner
            showBanner = true;
        } else {
            showBanner = geoCookie !== storefront && !previouslyDismissedBanner;
        }

        if (showBanner) {
            // if storefront name can not be translated in current language, choose the default translation
            const translatedGeoSfName =
                storefrontNameTranslations[geoCookie][currentLanguage] ??
                storefrontNameTranslations[geoCookie]['default'];

            // if user is logged in, show them their account storefront as a dropdown option
            const translatedAccountSfName = user?.userStorefront
                ? storefrontNameTranslations[user.userStorefront][
                      currentLanguage
                  ] ??
                  storefrontNameTranslations[user.userStorefront]['default']
                : null;

            const shouldShowAccountSf =
                translatedAccountSfName && user?.userStorefront !== geoCookie;

            // away from home dropdown menu should show an option to launch the
            // locale switcher banner, your detected home geo, and detected logged in sf
            options = [
                {
                    name: translatedGeoSfName,
                    callback: () => {
                        window.location.assign(
                            getStorefrontRoute(defaultRoute, geoCookie),
                        );
                    },
                },
                {
                    name: translateFn('AMP.Shared.LocaleSwitcher.Heading'),
                    callback: (e: Event) => {
                        modalTriggerElement = e.target;
                        modalElement.showModal();
                    },
                },
            ];

            // add account storefront as first option if !== geo cookie
            if (shouldShowAccountSf)
                options.unshift({
                    name: translatedAccountSfName,
                    callback: () => {
                        window.location.assign(
                            getStorefrontRoute(
                                defaultRoute,
                                user?.userStorefront,
                            ),
                        );
                    },
                });
        }
    };

    onMount(() => inititalizeComponent());
</script>

{#if showBanner}
    <div
        aria-label={translateFn('AMP.Shared.AwayFromHomeBanner.AriaLabel')}
        class="banner-container"
        data-testid="banner-container"
        use:focusNodeOnMount
    >
        <div class="banner-content">
            <button
                class="close-button"
                data-testid="close-button"
                type="button"
                on:click={handleCloseButton}
                aria-label={translateFn('AMP.Shared.AX.Close')}
            >
                <CloseIcon />
            </button>
            <div class="dropdown-container">
                <span class="message">
                    {translateFn('AMP.Shared.AwayFromHomeBanner.Heading')}
                </span>
                <DropdownMenu
                    {translateFn}
                    position={dropdownPosition}
                    {options}
                />
            </div>
        </div>
    </div>
{/if}

<Modal
    {modalTriggerElement}
    bind:this={modalElement}
    ariaLabelledBy={dialogTitleId}
>
    <LocaleSwitcherModal
        {translateFn}
        {regions}
        {defaultRoute}
        {dialogTitleId}
        on:close={modalElement.close}
    />
</Modal>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .banner-container {
        // TODO: rdar://98856238 (JMOTW: Sidebar content overlaps banners on short viewports)
        padding: 15px 25px 20px;
        display: grid;
        grid-area: structure-locale-switcher;
        background: var(--bannerDarkGray, rgba(68, 68, 68, 1));
        color: var(--systemPrimary-onDark);
        position: relative;
        z-index: calc(var(--z-web-chrome) + 1);
        padding-inline-start: 24px;
        padding-inline-end: 32px;

        @media (--small) {
            min-height: 74px;
        }

        @media (--range-sidebar-hidden-down) {
            margin-top: -$global-player-bar-height; // compensate for player bar padding
        }

        @media (--sidebar-visible) {
            // Stacking context above sidebar navigation
            z-index: calc(var(--z-web-chrome) - 10);
        }

        @media (--range-grid-layout-xs-1-sidebar-down) {
            padding-top: 12px;
            min-height: 125px;
        }
    }

    .banner-content {
        margin: 0 auto;
        justify-content: center;
        display: flex;
        align-items: center;
        width: 100%;
        max-width: 1884px;
        height: 100%;

        @media (--range-grid-layout-xs-1-sidebar-down) {
            justify-content: space-between;
            align-items: flex-start;
        }
    }

    .close-button {
        line-height: 0; // Eliminate white space
        fill: var(--systemPrimary-onDark);

        @media (--range-xsmall-down) {
            position: absolute;
            top: 17px;
            inset-inline-start: 25px;
            z-index: var(--z-default);
        }
    }

    // shadow DOM styles
    :global(:host) {
        .close-button {
            @media (--range-xsmall-down) {
                inset-inline-start: 25px;
            }
        }
    }

    .dropdown-container {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        width: 100%;
        padding-inline-start: 20px;

        @media (--range-grid-layout-xs-1-sidebar-down) {
            justify-content: space-between;
            height: 100%;
        }

        @media (--range-xsmall-down) {
            --overridSelectContainerWidth: 100%;
            flex-direction: column;
            align-items: flex-start;
            padding-inline-start: 0;

            :global(.dropdown-button) {
                flex-grow: 1;
            }
        }
    }

    .message {
        font: var(--title-3);
        margin-inline-end: 30px;

        @media (--range-xsmall-down) {
            margin-bottom: 20px;
            padding-inline-start: 40px;
            margin-inline-end: 20px;
        }
    }
</style>
